import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

export interface ItemCarrinho {
  sku: string
  descricao: string
  preco: number
  unidade: string
  quantidade: number
  foto: string | null
  estoqueDisponivel: number
}

interface CarrinhoState {
  // Map: slug → itens (carrinho por empresa)
  carrinhosPorLoja: Record<string, ItemCarrinho[]>

  getItens: (slug: string) => ItemCarrinho[]
  getTotal: (slug: string) => number
  getTotalItens: (slug: string) => number

  adicionar: (slug: string, item: ItemCarrinho) => void
  atualizarQuantidade: (slug: string, sku: string, qtd: number) => void
  remover: (slug: string, sku: string) => void
  limpar: (slug: string) => void
}

export const useCarrinho = create<CarrinhoState>()(
  persist(
    (set, get) => ({
      carrinhosPorLoja: {},

      getItens: (slug) => get().carrinhosPorLoja[slug] || [],

      getTotal: (slug) => {
        const itens = get().carrinhosPorLoja[slug] || []
        return itens.reduce((acc, it) => acc + it.preco * it.quantidade, 0)
      },

      getTotalItens: (slug) => {
        const itens = get().carrinhosPorLoja[slug] || []
        return itens.reduce((acc, it) => acc + it.quantidade, 0)
      },

      adicionar: (slug, item) => set((state) => {
        const atual = state.carrinhosPorLoja[slug] || []
        const existente = atual.find((it) => it.sku === item.sku)
        let novo: ItemCarrinho[]
        if (existente) {
          // incrementa mas respeita estoque
          const novaQtd = Math.min(
            existente.quantidade + item.quantidade,
            item.estoqueDisponivel,
          )
          novo = atual.map((it) =>
            it.sku === item.sku ? { ...it, quantidade: novaQtd } : it,
          )
        } else {
          novo = [...atual, item]
        }
        return {
          carrinhosPorLoja: { ...state.carrinhosPorLoja, [slug]: novo },
        }
      }),

      atualizarQuantidade: (slug, sku, qtd) => set((state) => {
        const atual = state.carrinhosPorLoja[slug] || []
        if (qtd <= 0) {
          return {
            carrinhosPorLoja: {
              ...state.carrinhosPorLoja,
              [slug]: atual.filter((it) => it.sku !== sku),
            },
          }
        }
        const novo = atual.map((it) => {
          if (it.sku !== sku) return it
          return { ...it, quantidade: Math.min(qtd, it.estoqueDisponivel) }
        })
        return {
          carrinhosPorLoja: { ...state.carrinhosPorLoja, [slug]: novo },
        }
      }),

      remover: (slug, sku) => set((state) => {
        const atual = state.carrinhosPorLoja[slug] || []
        return {
          carrinhosPorLoja: {
            ...state.carrinhosPorLoja,
            [slug]: atual.filter((it) => it.sku !== sku),
          },
        }
      }),

      limpar: (slug) => set((state) => ({
        carrinhosPorLoja: { ...state.carrinhosPorLoja, [slug]: [] },
      })),
    }),
    {
      name: 'pain_loja_carrinho',
      storage: createJSONStorage(() => localStorage),
    },
  ),
)
